import { Drawer, Title } from "@mantine/core"
import { zodResolver } from "@mantine/form"
import { Loader } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { ContactResponses } from "@tfg/types/endpoints/contacts"
import { CountryIso3166Schema, LanguageIso6391 } from "@tfg/types/schemas/enum"
import { standardEmail } from "@tfg/types/schemas/utils"

import {
  FormConfig,
  FormFieldConfig,
  TypesForm,
} from "~/pages/Admin/components/TypesForm.tsx"
import { useCreateContactMutation } from "~/pages/SupplierPortal/Contacts/hooks/useCreateContactMutation.ts"
import { useEditContactMutation } from "~/pages/SupplierPortal/Contacts/hooks/useEditContactMutation.ts"
import {
  errorMap,
  StringNoSpecialCharactersSchema,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap.tsx"

interface Props {
  opened: boolean
  onClose: () => void
  onContactCreated: (contactId: number) => void
  action?: "create" | "edit"
  contact?: ContactResponses.Contact
  contactInitialValues?: contactFormType
}

export const contactFormSchema = z.object({
  name: StringNoSpecialCharactersSchema,
  email: standardEmail(),
  preferredLanguage: z.nativeEnum(LanguageIso6391),
  companyName: z.string({ errorMap }).min(1).max(100),
  companyStreet: z.string({ errorMap }).nullable(),
  companyCity: z.string({ errorMap }).nullable(),
  companyPostalCode: z.string({ errorMap }).nullable(),
  companyCountry: CountryIso3166Schema.nullable(),
})

type contactFormType = z.infer<typeof contactFormSchema>

const INITIAL_VALUES: contactFormType = {
  name: "",
  email: "",
  preferredLanguage: LanguageIso6391.EN,
  companyName: "",
  companyCountry: CountryIso3166Schema.enum.FR,
  companyStreet: "",
  companyCity: "",
  companyPostalCode: "",
}
export const ContactDrawer = ({
  opened,
  onClose,
  onContactCreated,
  action = "create",
  contact,
  contactInitialValues = INITIAL_VALUES,
}: Props) => {
  const { t } = useTranslation(["traceability"])
  const { mutate: createContact, isLoading: isCreating } =
    useCreateContactMutation({
      onSuccess: (data) => {
        onContactCreated(data)
        handleClose()
      },
    })
  const { mutate: updateContact, isLoading: isUpdating } =
    useEditContactMutation(contact?.id ?? 0, {
      onSuccess: () => {
        handleClose()
      },
    })
  const [initialValues, setInitialValues] =
    useState<contactFormType>(contactInitialValues)
  const [reactFormConfig, setReactFormConfig] = useState<
    FormConfig<contactFormType>
  >({
    validate: zodResolver(contactFormSchema),
    initialValues,
  })
  useEffect(() => {
    setInitialValues(
      contact
        ? {
            name: contact.user.name,
            email: contact.user.email,
            preferredLanguage: contact.user.preferedLangageIso6391,
            companyName: contact.user.company.name,
            companyStreet: contact.user.company.street,
            companyCity: contact.user.company.city,
            companyPostalCode: contact.user.company.postalCode,
            companyCountry: contact.user.company.country,
          }
        : { ...INITIAL_VALUES }
    )
  }, [contact])
  useEffect(() => {
    setReactFormConfig({
      validate: zodResolver(contactFormSchema),
      initialValues,
    })
  }, [initialValues])

  const fields: FormFieldConfig[] = [
    {
      type: "text",
      name: "name",
      label: t("contactModal.labels.name") + " *",
      placeholder: t("contactModal.labels.namePlaceholder"),
    },
    {
      type: "text",
      name: "email",
      label: t("contactModal.labels.email") + " *",
      placeholder: t("contactModal.labels.emailPlaceholder"),
    },
    {
      type: "languageSelect",
      name: "preferredLanguage",
      label: t("contactModal.labels.preferredLanguage") + " *",
      placeholder: LanguageIso6391.FR,
    },
    {
      type: "text",
      name: "companyName",
      label: t("contactModal.labels.companyName") + " *",
      placeholder: t("contactModal.labels.companyNamePlaceholder"),
    },
    {
      type: "text",
      name: "companyStreet",
      label: t("contactModal.labels.companyStreet"),
      placeholder: t("contactModal.labels.companyStreetPlaceholder"),
    },
    {
      type: "text",
      name: "companyCity",
      label: t("contactModal.labels.companyCity"),
      placeholder: t("contactModal.labels.companyCityPlaceholder"),
    },
    {
      type: "text",
      name: "companyPostalCode",
      label: t("contactModal.labels.companyPostalCode"),
      placeholder: t("contactModal.labels.companyPostalCodePlaceholder"),
    },
    {
      type: "countrySelect",
      name: "companyCountry",
      label: t("contactModal.labels.companyCountry"),
      placeholder: CountryIso3166Schema.enum.FR,
    },
  ]

  const handleClose = () => {
    onClose()
  }

  return (
    <Drawer
      position="right"
      opened={opened}
      onClose={handleClose}
      title={<Title order={3}>{t("contactModal.title")}</Title>}
      size="xl"
    >
      {!isCreating || isUpdating ? (
        <TypesForm
          fields={fields}
          formConfig={reactFormConfig}
          mutate={action === "create" ? createContact : updateContact}
          submitLabel={t("contactModal.actions.create")}
          closeDrawer={onClose}
        />
      ) : (
        <Loader fullPage />
      )}
    </Drawer>
  )
}
